import { createFileRoute } from '@tanstack/react-router';
import {
  Check,
  Loader2,
  Plus,
  Radio,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  BackLink,
  Badge,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Textarea,
} from '../../../../../components/ui';
import { QueueCard } from '../../../../../components/club/QueueCard';
import { trpc } from '../../../../../lib/trpc';

import type { QueueUserStats } from '../../../../../components/club/QueueCard';

export const Route = createFileRoute('/app/clubs/$slug/queues/')({
  component: QueueListPage,
});

function QueueListPage() {
  const { slug } = Route.useParams();

  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const clubId = membership?.id;
  const isAdmin = membership?.role === 'owner' || membership?.role === 'admin';

  const { data: me } = trpc.user.me.useQuery();
  const { data: club } = trpc.club.get.useQuery(
    { clubId: clubId ?? '' },
    { enabled: !!clubId },
  );
  const placementMatchCount = club?.placementMatchCount ?? 10;

  const { data: games } = trpc.clubGame.list.useQuery(
    { clubId: clubId ?? '' },
    { enabled: !!clubId },
  );

  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const activeGameId = selectedGameId ?? games?.[0]?.id ?? null;
  const selectedGame = games?.find((g) => g.id === activeGameId);

  const { data: queues, isLoading } = trpc.queue.list.useQuery(
    { clubId: clubId ?? '', clubGameId: activeGameId ?? undefined },
    { enabled: !!clubId },
  );

  // Fetch user stats for all queues
  const { data: myStats } = trpc.clubMember.getStats.useQuery(
    { clubId: clubId ?? '', userId: me?.id ?? '' },
    { enabled: !!clubId && !!me?.id },
  );

  const enabledQueueIds = useMemo(
    () => (queues ?? []).map((q) => q.id),
    [queues],
  );

  // Batch-fetch queue status
  const { data: batchStatus } = trpc.queueEntry.batchStatus.useQuery(
    { queueIds: enabledQueueIds },
    { enabled: enabledQueueIds.length > 0 },
  );

  // Batch-fetch sparklines
  const { data: sparklines, isLoading: sparklinesLoading } = trpc.clubMember.getEloSparklines.useQuery(
    { queueIds: enabledQueueIds },
    { enabled: enabledQueueIds.length > 0, staleTime: 1000 * 60 },
  );

  const statsByQueue = useMemo(() => {
    const map = new Map<string, QueueUserStats>();
    for (const s of myStats ?? []) {
      map.set(s.queueId, {
        displayElo: s.displayElo,
        isPlacement: s.isPlacement,
        matchesPlayed: s.matchesPlayed,
        wins: s.wins,
        losses: s.losses,
        rank: s.rank,
        division: s.division,
        position: s.position,
      });
    }
    return map;
  }, [myStats]);

  const [showCreate, setShowCreate] = useState(false);

  if (!clubId) {
    return (
      <div className="max-w-[1400px] mx-auto p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-surface-800/50 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-5">
      <BackLink to="/app/clubs/$slug" params={{ slug }}>
        Back to Club
      </BackLink>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-bold text-white">Queues</h1>
        {isAdmin && (
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus className="size-4" />}
            onClick={() => setShowCreate(true)}
          >
            Create Queue
          </Button>
        )}
      </div>

      {/* Game filter */}
      {games && games.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedGameId(null)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              !selectedGameId
                ? 'bg-brand-500/15 text-brand-400 border border-brand-500/30'
                : 'bg-surface-800/50 text-surface-400 border border-surface-700/50 hover:text-white'
            }`}
          >
            All Games
          </button>
          {games.map((game) => (
            <button
              key={game.id}
              onClick={() => setSelectedGameId(game.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedGameId === game.id
                  ? 'bg-brand-500/15 text-brand-400 border border-brand-500/30'
                  : 'bg-surface-800/50 text-surface-400 border border-surface-700/50 hover:text-white'
              }`}
            >
              {game.name}
            </button>
          ))}
        </div>
      )}

      {/* Queue list */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-surface-800/50 animate-pulse" />
          ))}
        </div>
      ) : !queues || queues.length === 0 ? (
        <div className="flex items-center gap-3 p-6 rounded-xl bg-surface-800/30 border border-surface-700/30">
          <Radio className="size-5 text-surface-500 shrink-0" />
          <div>
            <p className="text-sm font-medium text-surface-300">No queues yet</p>
            <p className="text-xs text-surface-500 mt-0.5">
              {isAdmin ? 'Create a queue to start matchmaking.' : 'Queues will appear here once an admin creates them.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {queues.map((queue) => (
            <QueueCard
              key={queue.id}
              queue={queue}
              clubId={clubId}
              clubGameId={activeGameId ?? ''}
              gameName={selectedGame?.name ?? ''}
              userStats={statsByQueue.get(queue.id)}
              placementMatchCount={placementMatchCount}
              sparklineData={sparklines?.[queue.id]}
              sparklineLoading={sparklinesLoading}
              slug={slug}
              userId={me?.id}
              queueStatus={batchStatus?.[queue.id]}
            />
          ))}
        </div>
      )}

      {/* Create queue modal */}
      {showCreate && clubId && (
        <CreateQueueModal
          clubId={clubId}
          games={games ?? []}
          slug={slug}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Queue Modal
// ---------------------------------------------------------------------------

type GameOption = { id: string; name: string; teamSize?: number | null };

function CreateQueueModal({
  clubId,
  games,
  slug,
  onClose,
}: {
  clubId: string;
  games: GameOption[];
  slug: string;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clubGameId, setClubGameId] = useState(games[0]?.id ?? '');
  const [teamSize, setTeamSize] = useState<number | undefined>(undefined);
  const [matchSize, setMatchSize] = useState<number | undefined>(undefined);
  const [teamCount, setTeamCount] = useState<number>(2);

  const createMutation = trpc.queue.create.useMutation({
    onSuccess: () => {
      toast.success('Queue created');
      utils.queue.list.invalidate();
      onClose();
    },
    onError: (err) => toast.error(err.message || 'Failed to create queue'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !clubGameId) return;

    createMutation.mutate({
      clubId,
      clubGameId,
      name: name.trim(),
      description: description.trim() || undefined,
      teamSize,
      matchSize,
      teamCount,
    });
  };

  const isPending = createMutation.isPending;

  return (
    <Modal open={true} onClose={onClose} className="!max-w-md">
      <form onSubmit={handleSubmit}>
        <div className="px-6 pt-6 pb-1">
          <h2 className="text-xl font-display font-bold text-white">Create Queue</h2>
          <p className="text-sm text-surface-400 mt-1">Set up a new matchmaking queue</p>
        </div>
        <ModalBody className="space-y-4">
          {games.length > 1 && (
            <div>
              <label className="block text-xs font-medium text-surface-400 mb-1.5">Game</label>
              <select
                value={clubGameId}
                onChange={(e) => setClubGameId(e.target.value)}
                className="w-full rounded-lg bg-surface-800 border border-surface-600 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              >
                {games.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}

          <Input
            label="Queue Name"
            placeholder="e.g. Ranked 5v5"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            autoFocus
          />

          <Textarea
            label="Description"
            placeholder="Optional description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={2}
          />

          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Team Size"
              type="number"
              placeholder="e.g. 5"
              value={teamSize ?? ''}
              onChange={(e) => setTeamSize(e.target.value ? Number(e.target.value) : undefined)}
              min={1}
              max={50}
            />
            <Input
              label="Match Size"
              type="number"
              placeholder="e.g. 10"
              value={matchSize ?? ''}
              onChange={(e) => setMatchSize(e.target.value ? Number(e.target.value) : undefined)}
              min={2}
              max={100}
            />
            <Input
              label="Teams"
              type="number"
              value={teamCount}
              onChange={(e) => setTeamCount(Number(e.target.value) || 2)}
              min={2}
              max={10}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" type="button" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!name.trim() || !clubGameId || isPending}
            leftIcon={isPending ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
          >
            {isPending ? 'Creating...' : 'Create Queue'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
